import { AlertTriangle } from 'lucide-react';
import { useMemo } from 'react';
import type {
  ItemLaborView,
  PurchaseLineView,
} from '@obraplan/core/application/contracts/purchase-lines';
import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { formatBusinessDate } from '@/lib/format';
import {
  BASIS_HELP,
  BASIS_LABEL,
  formatHours,
  formatPurchaseQuantity,
  plural,
} from '@/features/purchase-lines/purchase-model';

/**
 * O que o item do cronograma por item pede de fora dele (Fase 26): os **materiais** da mesma
 * frente e serviço, com a data em que cada um precisa estar na obra (RN-342), e a **mão de
 * obra por função** (ADR-171, RN-351).
 *
 * O painel não busca nada: recebe as duas leituras já feitas (ou a espera, ou a recusa), para
 * a tela dizer o que aconteceu em vez de ficar muda — e para ser testado sem servidor.
 */

/** O estado de uma leitura: ainda indo, recusada, ou pronta. */
export type Loadable<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly message: string }
  | { readonly state: 'ready'; readonly value: T };

export interface ItemMaterials {
  readonly lines: readonly PurchaseLineView[];
  /** O servidor tem mais linhas do que as mostradas aqui. */
  readonly truncated: boolean;
}

export interface ItemDetailsProps {
  readonly item: ScheduleItemView;
  readonly materials: Loadable<ItemMaterials>;
  readonly labor: Loadable<readonly ItemLaborView[]>;
}

/** A tolerância da soma das funções contra o HH do item: a mesma do servidor (RN-351). */
const HOURS_TOLERANCE = 0.01;

export function ItemDetails({ item, materials, labor }: ItemDetailsProps) {
  return (
    <div className="flex flex-col gap-6">
      <MaterialsSection item={item} materials={materials} />
      <LaborSection item={item} labor={labor} />
    </div>
  );
}

// ── Materiais deste item ─────────────────────────────────────────────────────────────

function MaterialsSection({
  item,
  materials,
}: {
  readonly item: ScheduleItemView;
  readonly materials: Loadable<ItemMaterials>;
}) {
  const sorted = useMemo(
    () =>
      materials.state === 'ready'
        ? [...materials.value.lines].sort(
            (a, b) =>
              a.requiredDate.localeCompare(b.requiredDate) ||
              a.materialName.localeCompare(b.materialName, 'pt-BR'),
          )
        : [],
    [materials],
  );

  return (
    <section aria-labelledby="item-materials" className="flex flex-col gap-2">
      <div>
        <h3 id="item-materials" className="text-sm font-medium">
          Materiais deste item
        </h3>
        <p className="text-xs text-(--color-content-muted)">
          As linhas de compra da mesma frente e serviço. A data de cada uma é o início do item menos
          a margem de chegada da obra (RN-342).
          {item.segment !== null && ' Valem para todos os trechos do item, não só para este.'}
        </p>
      </div>

      {materials.state === 'loading' && (
        <p role="status" className="text-sm text-(--color-content-muted)">
          Carregando os materiais…
        </p>
      )}
      {materials.state === 'error' && (
        <p role="alert" className="text-sm text-(--color-state-danger)">
          {materials.message}
        </p>
      )}

      {materials.state === 'ready' &&
        (sorted.length === 0 ? (
          <p className="text-sm text-(--color-content-muted)">
            Nenhuma linha de compra para este serviço. Importe a planilha de itens de compra no
            cronograma por item.
          </p>
        ) : (
          <>
            <p className="text-xs text-(--color-content-muted)">
              {plural(sorted.length, 'linha', 'linhas')}
              {materials.value.truncated && ' — mostrando só as primeiras, na ordem da data'}.
            </p>
            <TableWrapper className="max-h-64 overflow-y-auto">
              <Table className="min-w-[32rem]">
                <TableHeader sticky>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Material</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead>Necessário em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="align-top">
                        <span className="font-medium">{line.materialName}</span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          <span className="tabular text-xs text-(--color-content-muted)">
                            {line.materialCode}
                          </span>
                          {line.materialCodeProvisional && (
                            <Badge
                              tone="warn"
                              title="Material do orçamento ainda sem SKU: não vira requisição até ser vinculado ao SKU (RN-347)."
                            >
                              código provisório
                            </Badge>
                          )}
                          {line.kind === 'SERVICO_TERCEIRIZADO' && (
                            <Badge
                              tone="info"
                              title="Contratação de empreiteiro, e não compra de material (RN-349)."
                            >
                              terceirizado
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-right align-top">
                        <span className="tabular whitespace-nowrap">
                          {formatPurchaseQuantity(
                            line.quantity,
                            line.unit,
                            line.quantityProvisional,
                          )}
                        </span>
                        {(line.quantityProvisional || line.allocationBasis !== null) && (
                          <span className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
                            {line.quantityProvisional && (
                              <Badge
                                tone="warn"
                                title="Quantidade estimada: rateio ou levantamento incompleto (RN-345)."
                              >
                                provisória
                              </Badge>
                            )}
                            {line.allocationBasis !== null && (
                              <span
                                className="text-xs text-(--color-content-muted)"
                                title={BASIS_HELP[line.allocationBasis]}
                              >
                                {BASIS_LABEL[line.allocationBasis]}
                              </span>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <span className="tabular whitespace-nowrap">
                          {formatBusinessDate(line.requiredDate)}
                        </span>
                        {!line.dateFollowsItem && (
                          <span className="block text-xs text-(--color-content-muted)">
                            data fixa
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          </>
        ))}
    </section>
  );
}

// ── Mão de obra por função ──────────────────────────────────────────────────────────

function LaborSection({
  item,
  labor,
}: {
  readonly item: ScheduleItemView;
  readonly labor: Loadable<readonly ItemLaborView[]>;
}) {
  const total = useMemo(
    () => (labor.state === 'ready' ? labor.value.reduce((sum, row) => sum + row.hours, 0) : 0),
    [labor],
  );
  const diverges =
    labor.state === 'ready' &&
    labor.value.length > 0 &&
    item.laborHours !== null &&
    Math.abs(total - item.laborHours) > HOURS_TOLERANCE;

  return (
    <section aria-labelledby="item-labor" className="flex flex-col gap-2">
      <div>
        <h3 id="item-labor" className="text-sm font-medium">
          Mão de obra por função
        </h3>
        <p className="text-xs text-(--color-content-muted)">
          O HH do item aberto pelas funções do cadastro e pelos serviços de terceiros (RN-351).
        </p>
      </div>

      {labor.state === 'loading' && (
        <p role="status" className="text-sm text-(--color-content-muted)">
          Carregando a mão de obra…
        </p>
      )}
      {labor.state === 'error' && (
        <p role="alert" className="text-sm text-(--color-state-danger)">
          {labor.message}
        </p>
      )}

      {labor.state === 'ready' &&
        (labor.value.length === 0 ? (
          <p className="text-sm text-(--color-content-muted)">
            Nenhum HH por função para este item. Importe a aba &ldquo;Mão de obra&rdquo; da planilha
            de itens de compra.
          </p>
        ) : (
          <>
            <TableWrapper>
              <Table className="min-w-[20rem]">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Função</TableHead>
                    <TableHead className="text-right">HH</TableHead>
                    <TableHead className="text-right">Parte</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {labor.value.map((row) => (
                    <TableRow
                      key={`${row.kind}|${row.jobRoleId ?? row.thirdPartyDescription ?? ''}`}
                    >
                      <TableCell>
                        {row.kind === 'TERCEIROS' ? (
                          <span className="flex flex-wrap items-center gap-1.5">
                            {row.thirdPartyDescription}
                            <Badge
                              tone="info"
                              title="Serviço de terceiros: o HH é do empreiteiro, e não de uma função do cadastro (D9)."
                            >
                              terceiros
                            </Badge>
                          </span>
                        ) : (
                          row.jobRoleName
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular">{formatHours(row.hours)}</TableCell>
                      <TableCell className="text-right tabular text-(--color-content-muted)">
                        {total > 0 ? `${formatHours((row.hours / total) * 100)}%` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-medium hover:bg-transparent">
                    <TableCell>Total das funções</TableCell>
                    <TableCell className="text-right tabular">{formatHours(total)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableWrapper>
            {diverges && (
              <p role="note" className="flex items-start gap-2 text-xs text-(--color-state-warn)">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span>
                  A soma das funções ({formatHours(total)} HH) difere do HH do item (
                  {formatHours(item.laborHours ?? 0)} HH).
                </span>
              </p>
            )}
          </>
        ))}
    </section>
  );
}
